import { Box, Button, Switch } from "@mui/material";
import { CustomInput, CustomSelect } from "../../../components-new/common";
import { AssertionTargetOptions, scopeOptionList } from "../constants";
import { useDispatch, useSelector } from "react-redux";
import { setEditStep } from "../../../Redux/TestAuthoring/TestAuthoring";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import {
  useAllApiQuery,
  useDownloadedFileQuery,
  useRecordedTestDataListQuery,
  useUpdateAssertionQuery,
  useFileDataNameQuery,
  useResetIterationQuery,
} from "../../../useQuery";
import { useEffect, useState } from "react";
import { useNotification } from "../../../hooks";
import { HelpIconButton } from "../../../components-new/help/help-modal";
import TestCaseSearch from "../../../components-new/common/TestCaseSearch";
import ModuleSelector from "../../../components-new/common/ModuleSelector";

const AssertionSetup = ({ item }) => {
  const dispatch = useDispatch();
  const notify = useNotification();
  const { mutate: resetIteration } = useResetIterationQuery();
  const [selectedTestCase, setSelectedTestCase] = useState(item.DependentTC || "");
  const [fileNameType, setFileNameType] = useState("");
  const [uploadedFile, setUploadedFile] = useState(null);

  const selectedProductID = useSelector((state) => state.authoringSlice.selectedProduct);
  const selectedModuleID = useSelector((state) => state.authoringSlice.selectedComponent);
  const allTestSteps = useSelector((state) => state.executionReport.allTestSteps);

  const { data: downloadedFiles } = useDownloadedFileQuery(item.Test_Case_Id);
  const {
    data: refFiles,
    refetch: refetchFiles,
    isSuccess: isRefFilesSuccess,
  } = useFileDataNameQuery(selectedProductID);
  const { data: recordedTestDataList, refetch: refetchRecordedTestDataList } =
    useRecordedTestDataListQuery(item.idtest_step, selectedTestCase);

  const moduleKey = item.apimodule === 0 ? item.DependentModule : item.apimodule;

  const {
    data,
    mutate: updateAssertionData,
    isSuccess: isUpdateSuccess,
    isError,
  } = useUpdateAssertionQuery();

  const { data: allApi, refetch: refetchAllApi } = useAllApiQuery(moduleKey || selectedModuleID, {
    enabled: item.apimodule !== 0 || !item.DependentModule,
  });

  useEffect(() => {
    if (item.ValDevice === "DB" && isError) {
      notify("Please enter valid query", "error");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError]);

  useEffect(() => {
    if (item.ValDevice === "InRefrenceFile") {
      if (item.filename) {
        const simulatedEvent = {
          target: {
            value: item.filename,
          },
        };
        handleRefFileChange(simulatedEvent);
      }
      if (!item.scope) {
        handleChange("Single Run", "scope");
      }
    }

    if (item.ValDevice === "InFile") {
      if (item.filename) {
        const simulatedEvent = {
          target: {
            value: item.filename,
          },
        };
        handleRefFileChange(simulatedEvent);
      }
      if (!item.scope) {
        handleChange("Single Run", "scope");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefFilesSuccess, item.filename, item.ValDevice, item.scope]);

  useEffect(() => {
    const isDependencyMode =
      item.ValDevice === "UseVariableFromTheScenario" ||
      item.ValDevice === "DependentonOtherTestScenario";

    if (isDependencyMode && selectedTestCase !== "" && selectedTestCase !== 0) {
      refetchRecordedTestDataList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTestCase, item.ValDevice]);

  useEffect(() => {
    if (item.ValDevice === "UseVariableFromTheScenario") {
      setSelectedTestCase(item.Test_Case_Id);
    }
    if (item.ValDevice === "InRefrenceFile") {
      refetchFiles();
    }
  }, [item.Test_Case_Id, item.ValDevice, refetchFiles, refetchRecordedTestDataList]);

  useEffect(() => {
    if (
      item.ValDevice === "DependentonOtherTestScenario" &&
      item.DependentTC &&
      item.DependentTC !== 0
    ) {
      setSelectedTestCase(item.DependentTC);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.ValDevice, item.DependentTC]);

  useEffect(() => {
  if (isUpdateSuccess) {
    const updatedData = data?.data?.data;
    if (updatedData && Object.values(updatedData)?.length) {
      const findStepByNumber = Object.values(updatedData).find(
        (step) => step.Step_Number === item.Step_Number,
      );

      if (findStepByNumber) {
        dispatch(setEditStep({ ...item, ...findStepByNumber }));
      }
    }

    notify("The assertion target has been updated successfully.", "success");
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [isUpdateSuccess]);

  const handleChange = (value, field) => {
    dispatch(setEditStep({ ...item, [field]: value }));
  };

  const handleDateChange = (value, field, testdata) => {
    const parts = testdata.split("~");
    const index = field === "date" ? 2 : 3;
    parts[index] = value;
    const updatedTestData = parts.join("~");

    dispatch(setEditStep({ ...item, TestData: updatedTestData }));
  };

  const handleChangeMultiple = (value) => {
    dispatch(setEditStep({ ...item, ...value }));
  };

  const handleRefFileChange = (event) => {
    const filename = event.target.value;
    if (filename) {
      const parts = filename.split(".");
      const fileExtension = parts.pop();
      handleChangeMultiple({ filename: filename, fromFileType: fileExtension });
      setFileNameType(fileExtension);
    }
  };

  const handleFileChange = (event) => {
    if (event.target.files.length > 0) {
       const file = event.target.files[0];
       const filename = file.name;
      const parts = filename.split(".");
      const fileExtension = parts.pop();
      setUploadedFile(file);
      handleChangeMultiple({ filename: event.target.files[0].name, 
        fromFileType: fileExtension, 
        sheet: "",
        cell: "",
        endRange: "",
      });
      setFileNameType(fileExtension);
    }
  };

  const handleDownloadedFileChange = (event) => {
    const filename = event.target.value;
    const parts = filename.split(".");
    const fileExtension = parts.pop();
    handleChangeMultiple({ filename: event.target.value, fromFileType: fileExtension });
    setFileNameType(fileExtension);
  };

  const handleSubmit = () => {
  const isExcel =
    fileNameType === "xlsx" ||
    item.filename?.toLowerCase().endsWith(".xlsx");

  if (isExcel) {
    const isSheetEmpty = !item.sheet || item.sheet.trim() === "";
    const isCellEmpty = !item.cell || item.cell.trim() === "";

    if (isSheetEmpty && isCellEmpty) {
      notify("Sheet and Cell cannot be empty for Excel files.", "error");
      return;
    }

    if (isSheetEmpty) {
      notify("Sheet cannot be empty for Excel files.", "error");
      return;
    }

    if (isCellEmpty) {
      notify("Cell cannot be empty for Excel files.", "error");
      return;
    }
  }
  const file = uploadedFile;
  updateAssertionData({
    ...item,
    fileObject: file,
  });
};
  const handleResetIteration = () => {
    resetIteration(item, {
      onSuccess: () => notify("Iteration reset successfully", "success"),
      onError: () => notify("Failed to reset iteration", "error"),
    });
  };
  return (
    <Box>
      <Box mb={3}>
        <CustomSelect
          label={"Assertion Target"}
          options={AssertionTargetOptions}
          width={"60%"}
          background={"#ffffff"}
          value={item.ValDevice || "OnScreen"}
          onChange={(e) => handleChange(e.target.value, "ValDevice")}
        />
      </Box>
      {item.ValDevice === "InStreamedFile" && (
        <CustomInput
          placeholder="Enter Stream URL"
          labelinput="Stream URL"
          value={item.InStreamedFile || ""}
          onChange={(e) => handleChange(e.target.value, "InStreamedFile")}
          flex={1}
        />
      )}
      {item.ValDevice === "LastRun" && (
        <Box display="flex" columnGap={2}>
          <CustomSelect
            label={"Step Number"}
            options={(allTestSteps ?? []).map((step) => ({
              label: `Step ${step.Step_Number}`,
              value: step.idtest_step,
            }))}
            width={"300px"}
            background={"#ffffff"}
            value={item.lastRunStep || ""}
            onChange={(e) => handleChange(e.target.value, "lastRunStep")}
          />
          <CustomSelect
            label={"Select Field Name"}
            options={[{ label: "Test Data", value: "TestData" }]}
            width={"300px"}
            background={"#ffffff"}
            value={item.lastRunTestData || ""}
            onChange={(e) => handleChange(e.target.value, "lastRunTestData")}
            />
        </Box>
      )}
      {item.ValDevice === "FromEmail" && (
        <Box display="flex" columnGap={2}>
          <CustomInput
            onChange={(e) => handleChange(e.target.value, "customerEmail")}
            placeholder="Enter email"
            labelinput="Email"
            value={item.customerEmail || ""}
            flex={1}
          />
          <CustomInput
            type="password"
            placeholder="Enter password"
            labelinput="Password"
            value={item.customerPassword || ""}
            onChange={(e) => handleChange(e.target.value, "customerPassword")}
            flex={1}
          />
          <CustomInput
            placeholder="Enter criterion"
            labelinput="Selection Criterion"
            value={item.EmailSelectionCriteria || ""}
            onChange={(e) => handleChange(e.target.value, "EmailSelectionCriteria")}
            flex={1}
          />
          <CustomInput
            placeholder="Enter filter"
            labelinput="Filter"
            value={item.EmailFilter || ""}
            onChange={(e) => handleChange(e.target.value, "EmailFilter")}
            flex={1}
          />
        </Box>
      )}
      {item.ValDevice === "ChangeTestData" && (
        <CustomInput
          placeholder="Enter new data"
          labelinput="New Data"
          value={item.TestData || ""}
          onChange={(e) => handleChange(e.target.value, "TestData")}
          flex={1}
        />
      )}
      {item.ValDevice === "Date" && (
        <Box display="flex" columnGap={2}>
          <CustomInput
            placeholder="Enter date"
            labelinput="Date"
            value={item.TestData.split("~")[2] || ""}
            onChange={(e) => handleDateChange(e.target.value, "date", item.TestData)}
            flex={1}
          />
          <CustomInput
            placeholder="Enter date format"
            labelinput="Date format"
            value={item.TestData.split("~")[3] || ""}
            onChange={(e) => handleDateChange(e.target.value, "dateformat", item.TestData)}
            flex={1}
          />
        </Box>
      )}
      {item.ValDevice === "InApi" && (
        <Box display="flex" flexDirection="column" gap={2}>
          <ModuleSelector
            onModuleChange={(selection) => {
              if (selection) {
                handleChangeMultiple({
                  apiproduct: selection.idproducts,
                  apimodule: selection.idmodules,
                });

                // Optional: only if your query does NOT auto-trigger
                refetchAllApi();
              } else {
                handleChangeMultiple({
                  apiproduct: "",
                  apimodule: "",
                  apiid: "",
                  apiparam: "",
                  DBQuery: "",
                });
              }
            }}
            initialValue={
              item.apiproduct && item.apimodule
                ? {
                    idproducts: item.apiproduct,
                    idmodules: item.apimodule,
                  }
                : null
            }
            tabId="inApi"
          />

          {item.apimodule !== "" && item.apimodule !== 0 && (
            <Box display="flex" gap={2}>
              <CustomSelect
                label={"Api name"}
                options={(Array.isArray(allApi) ? allApi : []).map((apiItem) => ({
                  label: apiItem.name,
                  value: apiItem.idapi,
                }))}
                width={"100%"}
                background={"#ffffff"}
                value={item.apiid}
                onChange={(e) => {
                  handleChange(e.target.value, "apiid");
                }}
                disabled={!item.apimodule}
              />

              <CustomInput
                placeholder="Enter api Param"
                labelinput="Api Param"
                value={item.apiparam || ""}
                onChange={(e) => handleChange(e.target.value, "apiparam")}
                flex={1}
              />

              <CustomInput
                placeholder="Enter api query"
                labelinput="Api Query"
                value={item.DBQuery || ""}
                onChange={(e) => handleChange(e.target.value, "DBQuery")}
                flex={1}
              />
            </Box>
          )}
        </Box>
      )}
      {item.ValDevice === "DB" && (
        <CustomInput
          placeholder="Enter DB query"
          labelinput="DB Query"
          value={item.DBQuery || ""}
          onChange={(e) => handleChange(e.target.value, "DBQuery")}
          flex={1}
        />
      )}
      {item.ValDevice === "Regex" && (
        <CustomInput
          placeholder="Enter Regex"
          labelinput="Regex"
          value={item.regex || ""}
          onChange={(e) => handleChange(e.target.value, "regex")}
          flex={1}
        />
      )}
      {item.ValDevice === "InDownloadedFile" && (
        <Box display="flex" columnGap={2}>
          <CustomSelect
            label={"Downloaded Files"}
            options={(downloadedFiles ?? [{ label: "", item: "" }]).map((fileItem) => ({
              label: fileItem,
              value: fileItem,
            }))}
            width={"100%"}
            background={"#ffffff"}
            value={item?.filename?.split("~")[0]}
            onChange={(e) => {
              handleChange(e.target.value, "filename");
              handleDownloadedFileChange(e);
            }}
          />
          {(item?.filename?.includes(".xls") ||
            item?.filename?.includes(".xlsx") ||
            item?.filename?.includes(".csv")) && (
            <>
              <CustomInput
                placeholder="Sheet"
                labelinput="Sheet"
                value={item.sheet}
                onChange={(e) => handleChange(e.target.value, "sheet")}
              />
              <CustomInput
                placeholder="Cell"
                labelinput="Cell"
                value={item.cell}
                onChange={(e) => handleChange(e.target.value, "cell")}
              />
              <CustomSelect
                label={"Scope"}
                options={(scopeOptionList ?? [{ label: "", item: "" }]).map((scope) => ({
                  label: scope.value,
                  value: scope.label,
                }))}
                width={"100%"}
                background={"#ffffff"}
                value={item.scope || "Single Run"}
                onChange={(e) => handleChange(e.target.value, "scope")}
              />
              {item?.scope === scopeOptionList[0].value && (
                <CustomInput
                  placeholder="End Range"
                  labelinput="End Range"
                  value={item.endRange}
                  onChange={(e) => handleChange(e.target.value, "endRange")}
                />
              )}
              
            </>
          )}

          {/* ZIP handling 👇 */}
            {item?.filename?.toLowerCase().endsWith(".zip") && (
              <CustomInput
                placeholder="Enter file inside ZIP"
                labelinput="Inner File Name"
                value={item.innerFileName || ""}
                onChange={(e) => handleChange(e.target.value, "innerFileName")}
              />
            )}
        </Box>
      )}
      {item.ValDevice === "UseVariableFromTheScenario" && (
  <CustomSelect
    label={"Use var from this scenario"}
    options={
      Array.isArray(recordedTestDataList?.list) && recordedTestDataList.list.length > 0
        ? recordedTestDataList.list.map((fileItem) => ({
            label: fileItem.label || fileItem,
            value: fileItem.value || fileItem,
          }))
        : item.TestData
          ? [{ label: item.TestData, value: item.TestData }]
          : [{ label: "No data available", value: "" }]
    }
    width={"100%"}
    background={"#ffffff"}
    value={item.TestData}
    onChange={(e) => handleChange(e.target.value, "TestData")}
  />
)}
      {item.ValDevice === "InFile" && (
        <Box display="flex" columnGap={2} alignItems={"center"}>
          <Button
            sx={{ height: " 50px", marginTop: "10px", flexShrink: 0 }}
            size="medium"
            variant="contained"
            component={"label"}
            startIcon={<CloudUploadIcon />}
          >
            Upload file
             <input type="file" hidden onChange={handleFileChange} />
          </Button>
          <CustomInput
            labelinput="File Name"
            value={item.filename}
            variant="outlined"
            disabled
            InputProps={{
              readOnly: true,
            }}
            sx={{ marginTop: "10px", minWidth: 180 }}
          />
          {(fileNameType === "xlsx" || fileNameType === "csv") && (
            <>
              <CustomInput
                placeholder="Sheet"
                labelinput="Sheet"
                value={item.sheet}
                onChange={(e) => handleChange(e.target.value, "sheet")}
              />
              <CustomInput
                placeholder="Cell"
                labelinput="Cell"
                value={item.cell}
                onChange={(e) => handleChange(e.target.value, "cell")}
              />
              <CustomSelect
                label={"Scope"}
                options={(scopeOptionList ?? [{ label: "", item: "" }]).map((item) => ({
                  label: item.value,
                  value: item.label,
                }))}
                width={"100%"}
                background={"#ffffff"}
                value={item.scope || "Single Run"}
                onChange={(e) => handleChange(e.target.value, "scope")}
              />
              {item?.scope === scopeOptionList[0].value && (
                <CustomInput
                  placeholder="End Range"
                  labelinput="End Range"
                  value={item.endRange}
                  onChange={(e) => handleChange(e.target.value, "endRange")}
                />
              )}
              {item.scope === "Multi Execution" && (
               <>
                  <Box display="flex" alignItems="center" gap={1} sx={{ flexShrink: 0 }}>
                    <Switch
                      checked={item.resumePreviousRun || false}
                      onChange={(e) => handleChange(e.target.checked, "resumePreviousRun")}
                    />
                    <Box sx={{ fontSize: "14px" }}>
                      Resume Previous Run
                    </Box>
                  </Box>
                  <Button
                    variant="contained"
                    sx={{ height: 40, flexShrink: 0 }}
                    onClick={handleResetIteration}
                  >
                    RESET ITERATION
                  </Button>
                </>
              )}
            </>
          )}
          {fileNameType === "json" && (
            <>
              <CustomInput
                placeholder="Query"
                labelinput="Query"
                value={item.filter}
                onChange={(e) => handleChange(e.target.value, "filter")}
              />
              <CustomSelect
                label={"Scope"}
                options={(scopeOptionList ?? [{ label: "", item: "" }]).map((item) => ({
                  label: item.value,
                  value: item.label,
                }))}
                width={"100%"}
                background={"#ffffff"}
                value={item.scope || "Single Run"}
                onChange={(e) => handleChange(e.target.value, "scope")}
              />
              {item?.scope === scopeOptionList[0].value && (
                <CustomInput
                  placeholder="End Range"
                  labelinput="End Range"
                  value={item.endRange}
                  onChange={(e) => handleChange(e.target.value, "endRange")}
                />
              )}
            </>
          )}
        </Box>
      )}
      {item.ValDevice === "InGoogleSheet" && (
        <Box display="flex" columnGap={2}>
          <CustomInput
            placeholder="Sheet Id"
            labelinput="Sheet Id"
            value={item.filename}
            onChange={(e) => handleChange(e.target.value, "filename")}
          />
          <CustomInput
            placeholder="Sheet"
            labelinput="Sheet"
            value={item.sheet}
            onChange={(e) => handleChange(e.target.value, "sheet")}
          />
          <CustomInput
            placeholder="Cell"
            labelinput="Cell"
            value={item.cell}
            onChange={(e) => handleChange(e.target.value, "cell")}
          />
          <CustomSelect
            label={"Scope"}
            options={(scopeOptionList ?? [{ label: "", item: "" }]).map((item) => ({
              label: item.value,
              value: item.label,
            }))}
            width={"100%"}
            background={"#ffffff"}
            value={item.scope || "Single Run"}
            onChange={(e) => handleChange(e.target.value, "scope")}
          />
          {item?.scope === scopeOptionList[0].value && (
            <CustomInput
              placeholder="End Range"
              labelinput="End Range"
              value={item.endRange}
              onChange={(e) => handleChange(e.target.value, "endRange")}
            />
          )}
        </Box>
      )}
      {item.ValDevice === "InRefrenceFile" && (
        <Box display="flex" columnGap={2}>
          <CustomSelect
            label={"Select file"}
            options={
              Array.isArray(refFiles?.list) && refFiles?.list.length > 0
                ? refFiles.list.map((fileItem) => ({
                    label: fileItem.label || fileItem,
                    value: fileItem.value || fileItem,
                  }))
                : [{ label: "No files available", value: "" }]
            }
            width={"100%"}
            background={"#ffffff"}
            value={item.filename}
            onChange={handleRefFileChange}
          />
          {(fileNameType === "xlsx" || fileNameType === "csv") && (
            <>
              <CustomInput
                placeholder="Sheet"
                labelinput="Sheet"
                value={item.sheet}
                onChange={(e) => handleChange(e.target.value, "sheet")}
              />
              <CustomInput
                placeholder="Cell"
                labelinput="Cell"
                value={item.cell}
                onChange={(e) => handleChange(e.target.value, "cell")}
              />
              <CustomSelect
                label={"Scope"}
                options={(scopeOptionList ?? [{ label: "", item: "" }]).map((item) => ({
                  label: item.value,
                  value: item.label,
                }))}
                width={"100%"}
                background={"#ffffff"}
                value={item.scope || "Single Run"}
                onChange={(e) => handleChange(e.target.value, "scope")}
              />
              {item?.scope === scopeOptionList[0].value && (
                <CustomInput
                  placeholder="End Range"
                  labelinput="End Range"
                  value={item.endRange}
                  onChange={(e) => handleChange(e.target.value, "endRange")}
                />
              )}
              {item.scope === "Multi Execution" && (
               <>
                  <Box display="flex" alignItems="center" gap={1} sx={{ flexShrink: 0 }}>
                    <Switch
                      checked={item.resumePreviousRun || false}
                      onChange={(e) => handleChange(e.target.checked, "resumePreviousRun")}
                    />
                    <Box sx={{ fontSize: "14px" }}>
                      Resume Previous Run
                    </Box>
                  </Box>
                  <Button
                    variant="contained"
                    sx={{ height: 40, flexShrink: 0, alignSelf: "center" }}
                    onClick={handleResetIteration}
                  >
                    RESET ITERATION
                  </Button>
                </>
              )}
            </>
          )}
          {fileNameType === "json" && (
            <>
              <CustomInput
                placeholder="Query"
                labelinput="Query"
                value={item.filter}
                onChange={(e) => handleChange(e.target.value, "filter")}
              />
              <CustomSelect
                label={"Scope"}
                options={(scopeOptionList ?? [{ label: "", item: "" }]).map((item) => ({
                  label: item.value,
                  value: item.label,
                }))}
                width={"100%"}
                background={"#ffffff"}
                value={item.scope || "Single Run"}
                onChange={(e) => handleChange(e.target.value, "scope")}
              />
              {item?.scope === scopeOptionList[0].value && (
                <CustomInput
                  placeholder="End Range"
                  labelinput="End Range"
                  value={item.endRange}
                  onChange={(e) => handleChange(e.target.value, "endRange")}
                />
              )}
            </>
          )}
        </Box>
      )}
      {item.ValDevice === "FromFile" && (
        <Box display="flex" columnGap={2} alignItems={"center"}>
          <Button
            sx={{ height: " 50px", marginTop: "10px" }}
            size="medium"
            variant="contained"
            component={"label"}
            startIcon={<CloudUploadIcon />}
          >
            Upload file
            <input type="file" hidden onChange={handleFileChange} />
          </Button>
          <CustomInput
            label="File Name"
            value={item.filename}
            variant="outlined"
            disabled
            InputProps={{
              readOnly: true,
            }}
            sx={{ marginTop: "10px" }}
          />
          {(fileNameType === "xlsx" || fileNameType === "csv") && (
            <>
              <CustomInput
                placeholder="Sheet"
                labelinput="Sheet"
                value={item.sheet}
                onChange={(e) => handleChange(e.target.value, "sheet")}
              />
              <CustomInput
                placeholder="Cell"
                labelinput="Cell"
                value={item.cell}
                onChange={(e) => handleChange(e.target.value, "cell")}
              />
              <CustomSelect
                label={"Scope"}
                options={(scopeOptionList ?? [{ label: "", item: "" }]).map((item) => ({
                  label: item.value,
                  value: item.label,
                }))}
                width={"100%"}
                background={"#ffffff"}
                value={item.scope || "Single Run"}
                onChange={(e) => handleChange(e.target.value, "scope")}
              />
              {item?.scope === scopeOptionList[0].value && (
                <CustomInput
                  placeholder="End Range"
                  labelinput="End Range"
                  value={item.endRange}
                  onChange={(e) => handleChange(e.target.value, "endRange")}
                />
              )}
            </>
          )}
          {fileNameType === "json" && (
            <>
              <CustomInput
                placeholder="Query"
                labelinput="Query"
                value={item.filter}
                onChange={(e) => handleChange(e.target.value, "filter")}
              />
              <CustomSelect
                label={"Scope"}
                options={(scopeOptionList ?? [{ label: "", item: "" }]).map((item) => ({
                  label: item.value,
                  value: item.label,
                }))}
                width={"100%"}
                background={"#ffffff"}
                value={item.scope || "Single Run"}
                onChange={(e) => handleChange(e.target.value, "scope")}
              />
              {item?.scope === scopeOptionList[0].value && (
                <CustomInput
                  placeholder="End Range"
                  labelinput="End Range"
                  value={item.endRange}
                  onChange={(e) => handleChange(e.target.value, "endRange")}
                />
              )}
            </>
          )}
        </Box>
      )}
      {item.ValDevice === "DependentonOtherTestScenario" && (
        <Box display="flex" flexDirection="column" gap={2}>
          <TestCaseSearch
            onTestCaseSelect={(selectedTestCase) => {
              if (selectedTestCase) {
                handleChangeMultiple({
                  DependentTC: selectedTestCase.idtest_case,
                  DependentProduct: selectedTestCase.idproducts,
                  DependentModule: selectedTestCase.idmodules,
                  DependentTCName: selectedTestCase.Test_Case,
                  apiproduct: selectedTestCase.idproducts,
                  apimodule: selectedTestCase.idmodules,
                });
                setSelectedTestCase(selectedTestCase.idtest_case);
              } else {
                handleChangeMultiple({
                  DependentProduct: "",
                  DependentModule: "",
                  DependentTC: "",
                });
              }
            }}
            initialValue={
              item.DependentTC && item.DependentProduct && item.DependentModule
                ? {
                    idtest_case: item.DependentTC,
                    idproducts: item.DependentProduct,
                    idmodules: item.DependentModule,
                    Test_Case: item.DependentTCName || "",
                  }
                : null
            }
            tabId="assertionDependent"
          />

          {item.DependentTC !== "" && item.DependentTC !== 0 && (
            <CustomSelect
              label={"Use var from this scenario"}
              options={
  Array.isArray(recordedTestDataList?.list) && recordedTestDataList.list.length > 0
    ? recordedTestDataList.list.map((fileItem) => ({
        label: fileItem.label || fileItem,
        value: fileItem.value || fileItem,
      }))
    : item.TestData
      ? [{ label: item.TestData, value: item.TestData }]
      : [{ label: "No data available", value: "" }]
}
              width={"100%"}
              background={"#ffffff"}
              value={item.TestData}
              onChange={(e) => {
                handleChange(e.target.value, "TestData");
              }}
            />
          )}
        </Box>
      )}

      <Box mt={2} sx={{ display: "flex", flexDirection: "row", rowGap: 2 }}>
        <Button variant="contained" onClick={handleSubmit}>
          Submit
        </Button>
        <div className="ml-8">
          <HelpIconButton path="/assertion" />
        </div>
      </Box>
    </Box>
  );
};
export default AssertionSetup;
